import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { DetailsList, DetailsListLayoutMode, SelectionMode, IColumn, CommandBar, ICommandBarItemProps, Text, Shimmer, ShimmerElementType } from '@fluentui/react';
import { fetchWorkspaces, getWorkspaceUrls, type Workspace } from '../types/workspace';
import styles from './Workspaces.module.css';

export default function Workspaces() {
  const navigate = useNavigate();
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchWorkspaces()
      .then(setWorkspaces)
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load workspaces'))
      .finally(() => setLoading(false));
  }, []);

  const columns: IColumn[] = [
    {
      key: 'code', name: 'Code', fieldName: 'code', minWidth: 100, maxWidth: 140,
      onRender: (item: Workspace) => (
        <Link to={`/workspaces/${item.code}`} style={{ color: 'var(--ab-text-link)', textDecoration: 'none', fontWeight: 600 }}>
          {item.code}
        </Link>
      ),
    },
    {
      key: 'name', name: 'Name', fieldName: 'name', minWidth: 160, maxWidth: 300,
    },
    {
      key: 'adoProject', name: 'Azure DevOps', minWidth: 120, maxWidth: 200,
      onRender: (item: Workspace) => {
        const urls = getWorkspaceUrls(item.code);
        return (
          <a href={urls.adoProject} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--ab-text-link)', textDecoration: 'none' }}>
            {item.code}
          </a>
        );
      },
    },
    {
      key: 'sharepoint', name: 'SharePoint', minWidth: 100, maxWidth: 160,
      onRender: (item: Workspace) => {
        const urls = getWorkspaceUrls(item.code);
        return (
          <a href={urls.sharepointUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--ab-text-link)', textDecoration: 'none' }}>
            Team Site
          </a>
        );
      },
    },
  ];

  const commandBarItems: ICommandBarItemProps[] = [
    { key: 'refresh', text: 'Refresh', iconProps: { iconName: 'Refresh' }, onClick: () => { setLoading(true); fetchWorkspaces().then(setWorkspaces).catch(() => {}).finally(() => setLoading(false)); } },
  ];

  return (
    <div className={styles.page}>
      <CommandBar items={commandBarItems} styles={{ root: { borderBottom: '1px solid var(--ab-border)', background: 'var(--ab-bg-command-bar)', padding: '0 8px' } }} />

      <div className={styles.viewHeader}>
        <Text variant="xLarge" styles={{ root: { fontWeight: 600 } }}>Workspaces</Text>
        <Text variant="small" styles={{ root: { color: 'var(--ab-text-secondary)' } }}>
          {loading ? 'Loading…' : `${workspaces.length} records`}
        </Text>
      </div>

      {error && <div style={{ padding: '8px 20px', color: 'var(--ab-error)' }}>{error}</div>}

      {loading ? (
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {Array.from({ length: 8 }).map((_, i) => (
            <Shimmer key={i} shimmerElements={[{ type: ShimmerElementType.line, width: '100%', height: 32 }]} />
          ))}
        </div>
      ) : (
        <DetailsList
          items={workspaces}
          columns={columns}
          selectionMode={SelectionMode.none}
          layoutMode={DetailsListLayoutMode.justified}
          onItemInvoked={(item: Workspace) => navigate(`/workspaces/${item.code}`)}
          styles={{
            root: { overflow: 'auto', flex: 1 },
            headerWrapper: { position: 'sticky', top: 0, zIndex: 1 },
          }}
        />
      )}
    </div>
  );
}
